import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { laneApi, LaneTaskRow, LaneStatusCounts } from '../../api/laneApi';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { RINGFENCE_TASK_SOURCE } from '../../api/cancellationRingfence';
import { Claim } from '../../api/claims';
import { Button } from '../../components/ui';
import { LaneShell, LaneListSlotProps, LaneDetailSlotProps } from './LaneShell';
import {
  filtersFromSearchParams, applySelectedTask,
} from './laneUrlState';
import { toggleSelect } from './laneSelection';
import { agingLevel, agingLabel, parseBackendTimestamp, formatShortDate } from './aging';
import { ActorLabel } from './actorLabel';
import { TaskDetailPane } from './TaskDetailPane';
import { ClaimPickerModal } from './ClaimPickerModal';
import { MemberPickerModal } from './MemberPickerModal';
import { MultiRepSentByLabel } from './MultiRepSentByLabel';
import './lanes.css';

/**
 * Manual lane body — Tasks Rebuild Task 11. Replaces the legacy Tasks.tsx
 * "Manual" dropdown bucket. A flat, to-do-style list, built on the same
 * skeleton LenderLane (Task 10) proved out:
 *   - requestId latest-wins fetch guard
 *   - URL-backed sort/page/selected state
 *   - selection + bulk bar (Complete/Assign/Archive wired via Task 12)
 *   - j/k keyboard triage through the page's rows
 *
 * Default sort is createdAt ASC (oldest first) -- unlike Post/Lender, manual
 * tasks are a genuine to-do queue staff work down in the order they were
 * raised, not an inbox where the newest item matters most.
 *
 * Manual tasks have no email content: no sender, no received date, no AI
 * classification. The row is only ever subject/clientName/assignedClaimId/
 * createdAt, and the detail pane below notes that the `description` field
 * (a manual task's actual text body) arrives with Task 12's on-demand
 * content fetch -- it isn't part of the slim list row.
 */

/** Same DD/MM display idiom as LenderLane/PostLane -- the aging badge
 * (colour + title tooltip) carries the "how stale" signal. */
function formatDDMM(iso: string | null): string {
  if (!iso) return '—';
  const d = parseBackendTimestamp(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

type BulkAction = 'complete' | 'assign' | 'assignMember';

interface BulkProgress {
  action: BulkAction;
  done: number;
  total: number;
  failed: number;
}

// See LenderLane's identical constants for why 'complete' is pinned to this
// exact wording (matches pre-existing ManualLane.test.tsx assertions).
const BULK_ACTION_PROGRESS_VERB: Record<BulkAction, string> = {
  complete: 'Completing',
  assign: 'Assigning',
  assignMember: 'Assigning',
};
const BULK_ACTION_RESULT_SUFFIX: Record<BulkAction, string> = {
  complete: 'completed',
  assign: 'assigned',
  assignMember: 'assigned',
};

/** Bridges LaneShell's internal `refreshToken` up to this component's own
 * fetch effect -- same pattern as ClientLane/LenderLane/PostLane's
 * RefreshBridge. */
const RefreshBridge: React.FC<{ token: number; onChange: (t: number) => void }> = ({ token, onChange }) => {
  useEffect(() => { onChange(token); }, [token, onChange]);
  return null;
};

export const ManualLane: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  // Default sort per the brief: createdAt asc (oldest first -- a to-do
  // queue). No user-facing sort selector for this lane; the URL's
  // sortBy/sortOrder are still respected if ever set.
  const sortBy = filters.sortBy || 'createdAt';
  const sortOrder = filters.sortOrder || 'asc';

  const setSelectedTaskId = (taskId: string | null) => setSearchParams((prev) => applySelectedTask(prev, taskId));

  const [shellRefreshToken, setShellRefreshToken] = useState(0);

  const [rows, setRows] = useState<LaneTaskRow[]>([]);
  const [total, setTotal] = useState(0);
  // Task 11: pending/completed split for the toggle buttons -- see
  // LaneShell's `statusCounts` prop doc comment / LenderLane's identical
  // wiring.
  const [statusCounts, setStatusCounts] = useState<LaneStatusCounts | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowsById, setRowsById] = useState<Record<string, LaneTaskRow>>({});

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  // Outlives `bulkProgress`/`checkedIds` -- see LenderLane's identical
  // comment on why this can't be gated on `checkedIds.size > 0`.
  const [bulkResult, setBulkResult] = useState<{ action: BulkAction; total: number; failed: number } | null>(null);

  // Bulk assign dialog (Task 12) -- see LenderLane's identical state for the
  // shared ClaimPickerModal component. No Archive/Forward here: neither was
  // ever a stub on this lane's bulk bar (a manual to-do has no email content
  // to archive/reply to the way an inbound email does).
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  // Staff assignment restoration (fast-follow) -- see LenderLane's identical
  // state/comment for the shared MemberPickerModal component.
  const [bulkAssignMemberOpen, setBulkAssignMemberOpen] = useState(false);
  const [bulkAssigningMember, setBulkAssigningMember] = useState(false);

  // Latest-wins guard, same as LenderLane/PostLane's `requestIdRef`.
  const requestIdRef = useRef(0);

  const fetchRows = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    laneApi.getLaneTasks('manual', {
      statusTab: filters.tab,
      page: filters.page,
      pageSize: filters.pageSize,
      sortBy,
      sortOrder,
      // Task 13: omnibox operators thread through the same way LenderLane's
      // do -- see that file's identical additions for the reasoning. Manual
      // tasks have no sender/received-date (this file's doc comment above),
      // but claim/assigned/archived and free-text search still apply.
      senderEmail: filters.senderEmail || undefined,
      claim: filters.claim || undefined,
      assigned: filters.assigned || undefined,
      archived: filters.archived || undefined,
      q: filters.q || undefined,
      // Forced to `undefined` on the archived view -- the Task type dropdown
      // is hidden there (TaskTypeFilterDropdown disables itself), but that
      // doesn't reset the URL state, so without this override a taskSource
      // chosen on the live view would silently keep riding the wire after
      // the user toggles archived on. The backend happens to ignore it
      // against archived_email_tasks (no email_source column there), but the
      // frontend must enforce its own hygiene rather than rely on that.
      // Same pattern as LenderLane.tsx's aiProcessed override.
      taskSource: filters.archived ? undefined : (filters.taskSource || undefined),
    })
      .then((res) => {
        if (requestId !== requestIdRef.current) return; // stale response, dropped
        setRows(res.tasks);
        setTotal(res.total);
        setStatusCounts(res.statusCounts);
        setRowsById((prev) => {
          const next = { ...prev };
          res.tasks.forEach((r) => { next[r.id] = r; });
          return next;
        });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setError('Failed to load manual tasks.');
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.tab, filters.page, filters.pageSize, filters.q, sortBy, sortOrder,
    filters.senderEmail, filters.claim, filters.assigned, filters.archived, filters.taskSource,
  ]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows, shellRefreshToken]);

  // Selection + any leftover completion banner are transient UI state, not
  // URL state -- clear both whenever the page/tab/query changes, or any
  // omnibox-driven filter changes (Task 13) (same treatment as LenderLane/
  // PostLane).
  useEffect(() => {
    setCheckedIds(new Set());
    setBulkResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.tab, filters.page, filters.q, filters.senderEmail, filters.claim, filters.assigned, filters.archived, filters.taskSource]);

  const toggleChecked = (id: string) => {
    setBulkResult(null);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageChecked = rows.length > 0 && rows.every((r) => checkedIds.has(r.id));
  const toggleAllOnPage = () => {
    setBulkResult(null);
    setCheckedIds((prev) => {
      if (allOnPageChecked) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  // Shared per-item-failure-isolated bulk runner -- see LenderLane's
  // identical helper's doc comment.
  const runBulk = async (action: BulkAction, ids: string[], fn: (id: string) => Promise<any>) => {
    setBulkResult(null);
    setBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await fn(ids[i]);
      } catch {
        failed += 1;
      }
      setBulkProgress({ action, done: i + 1, total: ids.length, failed });
    }
    setBulkProgress(null);
    setBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchRows();
  };

  // Single-request variant of runBulk (perf fix -- see LenderLane's
  // identical helper's doc comment: POST /ui/team-tasks/bulk-assign already
  // accepts the full taskIds array and does one SQL UPDATE, so this drives
  // exactly one request instead of one-per-id. `fn` resolves to however many
  // ids the server actually matched (bulk_assign_tasks silently skips ids it
  // can't find rather than erroring), so "failed" is a partial *count*, not
  // a per-id try/catch outcome.
  const runBulkOneShot = async (action: BulkAction, ids: string[], fn: (ids: string[]) => Promise<number>) => {
    setBulkResult(null);
    setBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    try {
      const succeeded = await fn(ids);
      failed = Math.max(0, ids.length - succeeded);
    } catch {
      failed = ids.length;
    }
    setBulkProgress(null);
    setBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchRows();
  };

  const handleBulkComplete = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;
    const plural = ids.length === 1 ? '' : 's';
    const ok = window.confirm(`Complete ${ids.length} task${plural}?`);
    if (!ok) return;
    await runBulk('complete', ids, (id) => unifiedTasksApi.completeTask(id));
  };

  const handleBulkAssignConfirm = async (claim: Claim) => {
    const ids = Array.from(checkedIds);
    setBulkAssignOpen(false);
    if (ids.length === 0) return;
    setBulkAssigning(true);
    await runBulk('assign', ids, (id) => unifiedTasksApi.assignTask(id, claim.id, rowsById[id]?.emailType || undefined));
    setBulkAssigning(false);
  };

  const handleBulkAssignMemberConfirm = async (member: string | null) => {
    const ids = Array.from(checkedIds);
    setBulkAssignMemberOpen(false);
    if (ids.length === 0) return;
    setBulkAssigningMember(true);
    await runBulkOneShot('assignMember', ids, (allIds) => laneApi.assignMember(allIds, member).then((r) => r.assigned));
    setBulkAssigningMember(false);
  };

  // j/k triage through the current page's rows -- same as LenderLane/PostLane.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'j' && e.key !== 'k') return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (rows.length === 0) return;
      e.preventDefault();
      const currentIndex = rows.findIndex((r) => r.id === filters.selected);
      let nextIndex: number;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === 'j') {
        nextIndex = Math.min(rows.length - 1, currentIndex + 1);
      } else {
        nextIndex = Math.max(0, currentIndex - 1);
      }
      setSelectedTaskId(rows[nextIndex].id);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters.selected]);

  // Demo finding: "for completed emails we're missing a lot of the details
  // of who completed it and when it was completed." Aging (how long a task
  // waited before being actioned) is meaningless once it's done, so the
  // Completed sub-tab and the archived view both swap the aging badge for
  // completed-by/archived-by attribution instead -- archived takes priority
  // over completed when both apply (a row can be completed-then-archived;
  // the archive view is specifically about the archive action).
  const showCompletionInfo = filters.tab === 'completed' || filters.archived;

  const renderList = (slotProps: LaneListSlotProps) => (
      <div className="manual-lane">
        <RefreshBridge token={slotProps.refreshToken} onChange={setShellRefreshToken} />
        <div className="manual-lane__toolbar">
          {/* Archived browse is read-only (T13 follow-up 4b) -- bulk actions
              act on the live email_tasks table, not archived rows. */}
          {!filters.archived && (
            <label className="manual-lane__select-all">
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={allOnPageChecked}
                disabled={rows.length === 0}
                onChange={toggleAllOnPage}
              />
              Select all ({rows.length})
            </label>
          )}
        </div>

        {!filters.archived && checkedIds.size > 0 && (
          <div className="manual-lane__bulk-bar">
            <span className="manual-lane__bulk-count">{checkedIds.size} selected</span>
            <div className="manual-lane__bulk-actions">
              <Button size="sm" variant="secondary" onClick={() => setBulkAssignOpen(true)}>
                Assign claim
              </Button>
              <Button size="sm" variant="primary" onClick={handleBulkComplete}>
                Complete
              </Button>
              {/* Item 2 (lane-feedback batch 28/07, Ryan decision): "assign to
                  staff" is no longer admin-only -- any authenticated staff
                  member can assign a task to any roster member, matching the
                  backend's POST /ui/team-tasks/bulk-assign, which moved from
                  require_role(['admin']) to require_auth. Was `isAdmin &&`. */}
              <Button size="sm" variant="secondary" onClick={() => setBulkAssignMemberOpen(true)}>
                Assign to staff
              </Button>
            </div>
            {bulkProgress && (
              <span className="manual-lane__bulk-progress">
                {BULK_ACTION_PROGRESS_VERB[bulkProgress.action]} {bulkProgress.done}/{bulkProgress.total}
              </span>
            )}
          </div>
        )}

        {/* Deliberately NOT gated on checkedIds.size > 0 -- see LenderLane's
            identical comment: handleBulkComplete clears checkedIds in the
            same batch it sets this result. */}
        {bulkResult && (
          <div className="manual-lane__bulk-result" role="status">
            <span className="manual-lane__bulk-result-message">
              {bulkResult.failed > 0
                ? `${bulkResult.total - bulkResult.failed}/${bulkResult.total} ${BULK_ACTION_RESULT_SUFFIX[bulkResult.action]} — ${bulkResult.failed} failed`
                : `${bulkResult.total} ${BULK_ACTION_RESULT_SUFFIX[bulkResult.action]}`}
            </span>
            <button
              type="button"
              className="manual-lane__bulk-result-dismiss"
              aria-label="Dismiss completion result"
              onClick={() => setBulkResult(null)}
            >
              &times;
            </button>
          </div>
        )}

        {loading && (
          <div className="lane-skeleton-rows" aria-hidden="true">
            <div className="lane-skeleton-row" />
            <div className="lane-skeleton-row" />
            <div className="lane-skeleton-row" />
          </div>
        )}
        {error && <div className="manual-lane__error">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="manual-lane__empty">
            <div className="manual-lane__empty-title">
              {filters.archived ? 'No archived manual tasks' : 'No manual tasks'}
            </div>
            <div className="manual-lane__empty-body">
              {filters.archived
                ? 'No archived manual tasks match this search.'
                : (filters.q || filters.senderEmail || filters.claim || filters.assigned || filters.taskSource)
                  ? 'No manual tasks match the current filters.'
                  : 'Create one with "+ Create Task" above when something needs manual follow-up.'}
            </div>
          </div>
        )}

        <div className="manual-lane__rows">
          {/* Rows render in fetched order. The open task used to lift to row 1
              inside a sticky card; removed 2026-08-27 (Dec) along with the
              lanePinnedTasks preference. The selected TINT is what marks it now,
              and clicking it again still closes it. */}
          {rows.map((t) => {
            const level = agingLevel(t.createdAt);
            const isSelected = slotProps.selectedTaskId === t.id;
            const rowEl = (
              <div
                data-task-id={t.id}
                className={`manual-lane__row ${isSelected ? 'manual-lane__row--selected' : ''}`}
              >
                {!filters.archived && (
                  <input
                    type="checkbox"
                    className="manual-lane__row-checkbox"
                    aria-label={`Select ${t.subject || '(no subject)'}`}
                    checked={checkedIds.has(t.id)}
                    onChange={() => toggleChecked(t.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div
                  className="manual-lane__row-body"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask);
                  }}
                >
                  <div className="manual-lane__row-main">
                    <span className="manual-lane__row-subject">{t.subject || '(no subject)'}</span>
                    {t.clientName && <span className="manual-lane__row-client">{t.clientName}</span>}
                  </div>
                  <div className="manual-lane__row-side">
                    {t.assignedClaimId ? (
                      <span className="manual-lane__claim-chip">Claim {t.assignedClaimId}</span>
                    ) : (
                      <span className="manual-lane__no-claim-chip">No claim</span>
                    )}
                    {t.email_source === RINGFENCE_TASK_SOURCE && (
                      <span
                        className="manual-lane__tasktype-badge"
                        title="70+ cancellation review task"
                      >
                        70+
                      </span>
                    )}
                    {/* #682: the Multi-rep filter holds both senders; the row says which. */}
                    <MultiRepSentByLabel kind={t.manualKind} />
                    {showCompletionInfo ? (
                      <span className="lane-completed-info">
                        {filters.archived ? (
                          <>
                            {t.archivedBy && <ActorLabel raw={t.archivedBy} />}
                            {t.archivedAt && <span>{formatShortDate(t.archivedAt)}</span>}
                          </>
                        ) : (
                          <>
                            {t.completedBy && <ActorLabel raw={t.completedBy} />}
                            {t.completedAt && <span>{formatShortDate(t.completedAt)}</span>}
                          </>
                        )}
                      </span>
                    ) : (
                      <span
                        className={`manual-lane__aging manual-lane__aging--${level ?? 'unknown'}`}
                        title={`Waiting ${agingLabel(t.createdAt)}`}
                      >
                        {formatDDMM(t.createdAt)}
                      </span>
                    )}
                    {t.assignedToTeamMember && (
                      <span className="lane-assignee-pill">{t.assignedToTeamMember}</span>
                    )}
                  </div>
                </div>
              </div>
            );
            return <React.Fragment key={t.id}>{rowEl}</React.Fragment>;
          })}
        </div>
      </div>
  );

  const renderDetail = ({ selectedTaskId, showToast }: LaneDetailSlotProps) => (
    <TaskDetailPane
      taskId={selectedTaskId}
      onChanged={() => { fetchRows(); }}
      showToast={showToast}
      emptyMessage="Select a task to view it here."
      lane="manual"
    />
  );

  return (
    <>
      <LaneShell
        lane="manual"
        renderList={renderList}
        renderDetail={renderDetail}
        totalCount={total}
        statusCounts={statusCounts}
        // Bounded split height so the list and the open task scroll
        // independently and the selected row stays put, as on the Client lane.
        // Covers Completed as well as Pending -- LaneShell renders one split and
        // the sub-tabs only swap the data.
        viewportSplit
      />
      {bulkAssignOpen && (
        <ClaimPickerModal
          title="Assign to a claim"
          hint={`Assign ${checkedIds.size} selected task${checkedIds.size === 1 ? '' : 's'} to:`}
          confirmLabel="Assign"
          processing={bulkAssigning}
          onConfirm={handleBulkAssignConfirm}
          onClose={() => setBulkAssignOpen(false)}
        />
      )}
      {bulkAssignMemberOpen && (
        <MemberPickerModal
          title="Assign to a staff member"
          hint={`Assign ${checkedIds.size} selected task${checkedIds.size === 1 ? '' : 's'} to:`}
          confirmLabel="Assign"
          lane="manual"
          processing={bulkAssigningMember}
          onConfirm={handleBulkAssignMemberConfirm}
          onClose={() => setBulkAssignMemberOpen(false)}
        />
      )}
    </>
  );
};

export default ManualLane;
